<template>
  <div class="search-page">
    <div
      class="filter-container"
      :class="{expanded: filterOptionsExpanded}"
      role="search"
    >
      <div 
        class="filter-container-header"
        @click="filterOptionsExpanded = !filterOptionsExpanded"
      >
        Filter Results

        <span :class="filterOptionsExpanded ? 'up-arrow' : 'down-arrow'" />
      </div>
      <div
        class="filter-container-content"
        :class="{
          expanded: filterOptionsExpanded,
          collapsed: !filterOptionsExpanded,
        }"
      >
        <div class="input-group">
          <Dropdown
            v-model="buildTypeId"
            label="Build Type"
            :options="buildTypeDropdownOptions"
          />
          <CartridgeSearchMachine v-model="cartridgeId" />
        </div>
        <div class="button-wrapper">
          <button
            class="clear"
            @click="clearSearchForm"
          >
            Clear
          </button>
          <button
            class="search"
            @click="search"
          >
            Search
          </button>
        </div>
      </div>
    </div>
    <div class="results-container-header">
      <div class="results-container-header-text">
        My Quotes
      </div>
    </div>
    <LoadingPulse
      :loading="loading"
      class="loading-spinner"
    />
    <div
      v-if="!loading"
      class="overflow-wrapper"
    >
      <table>
        <thead>
          <tr>
            <th
              :tabindex="0"
              @keyup.enter="sortBy('BuildType')"
              @click="sortBy('BuildType')"
            >
              <div>
                Build Type
                <span
                  v-show="sortField === 'BuildType'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th
              :tabindex="0"
              @keyup.enter="sortBy('Cartridge')"
              @click="sortBy('Cartridge')"
            >
              <div>
                Cartridge
                <span
                  v-show="sortField === 'Cartridge'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th
              :tabindex="0"
              @keyup.enter="sortBy('ExpirationDate')"
              @click="sortBy('ExpirationDate')"
            >
              <div>
                Expiration Date
                <span
                  v-show="sortField === 'ExpirationDate'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody v-if="quotes.length > 0">
          <tr
            v-for="quote in quotes"
            :key="quote.id"
            class="clickable"
            :tabindex="0"
            @keyup.enter="onTableRowClicked(quote.id)"
            @click="onTableRowClicked(quote.id)"
          >
            <td>{{ quote.buildType }}</td>
            <td>{{ quote.cartridge }}</td>
            <td>{{ $methods.formatDate(quote.expirationDate) }}</td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td
              :colspan="3"
              class="message"
            >
              You currently do not have any quotes.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Paginator
      v-if="!loading"
      class="paginator"
      :total-results="totalResults"
      :page-number="pageNumber"
      :page-size="pageSize"
      @page-change="onPageChanged"
      @page-size-change="onPageSizeChanged"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Paginator from '@/components/shared/Paginator.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { CustomerQuote } from '@/@types/responses/customer_quote/CustomerQuote'
import Dropdown from '@/components/shared/Dropdown.vue'
import { BuildTypeService } from '@/services/buildType.service'
import CartridgeSearchMachine from '@/components/elements/CartridgeSearchMachine.vue'
import { BuildTypeDropdownOption } from '@/@types/responses/build_type/BuildTypeDropdownOption'
import { CustomerQuoteService } from '@/services/customerQuote.service'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'MyQuotes',
  components: {
    CartridgeSearchMachine,
    Dropdown,
    LoadingPulse,
    Paginator
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const customerQuoteService = inject(PROVIDER_CONSTANTS.customerQuoteService) as CustomerQuoteService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      buildTypeService,
      customerQuoteService,
      toast
    }
  },
  data() {
    let buildTypeId: number|undefined
    let cartridgeId: number|undefined

    return {
      ascending: true,
      buildTypeDropdownOptions: [] as BuildTypeDropdownOption[],
      buildTypeId,
      cartridgeId,
      filterOptionsExpanded: true,
      loading: true,
      pageNumber: 1,
      pageSize: 10,
      quotes: [] as CustomerQuote[],
      sortField: 'BuildType',
      totalResults: 0
    }
  },
  mounted() {
    this.fetchBuildTypeDropdownOptions().then(() => {
      this.parseQueryStringAndSearch()
    })
  },
  methods: {
    clearSearchForm(): void {
      this.buildTypeId = undefined
      this.cartridgeId = undefined
      this.search()
    },
    fetchBuildTypeDropdownOptions(): Promise<void> {
      return this.buildTypeService.getBuildTypeDropdownOptions().then(dropdownOptionsResponse => {
        this.buildTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to load your quotes. Try reloading the page.')
      })
    },
    getBooleanParameterByName(name: string): boolean | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? parameter === 'true' : undefined
    },
    getNumericParameterByName(name: string): number | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? Number(parameter) : undefined
    },
    getStringParameterByName(name: string): string | undefined {
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
      const results = regex.exec(window.location.href)

      if (!results || !results[2])
        return undefined
      
      return decodeURIComponent(results[2].replace(/\+/g, " "));
    },
    onPageChanged(pageNumber: number): void {
      this.pageNumber = pageNumber
      this.searchQuotes()
    },
    onPageSizeChanged(pageSize: number): void {
      this.pageSize = pageSize
      this.search()
    },
    onTableRowClicked(id: number): void {
      this.$router.push('/my-quote/' + id)
    },
    parseQueryStringAndSearch(): void {
      const ascendingFromUrl = this.getBooleanParameterByName("a")
      this.ascending = ascendingFromUrl !== undefined ? ascendingFromUrl : this.ascending

      const buildTypeIdFromUrl = this.getNumericParameterByName("bt")
      this.buildTypeId = buildTypeIdFromUrl !== undefined ? buildTypeIdFromUrl : this.buildTypeId

      const cartridgeIdFromUrl = this.getNumericParameterByName("ca")
      this.cartridgeId = cartridgeIdFromUrl !== undefined ? cartridgeIdFromUrl : this.cartridgeId

      const pageNumberFromUrl = this.getNumericParameterByName("pn")
      this.pageNumber = pageNumberFromUrl !== undefined ? pageNumberFromUrl : this.pageNumber

      const pageSizeFromUrl = this.getNumericParameterByName("ps")
      this.pageSize = pageSizeFromUrl !== undefined ? pageSizeFromUrl : this.pageSize

      const sortFieldFromUrl = this.getStringParameterByName("sf")
      this.sortField = sortFieldFromUrl !== undefined ? sortFieldFromUrl : this.sortField

      this.searchQuotes()
    },
    search(): void {
      this.pageNumber = 1
      this.searchQuotes()
    },
    searchQuotes(): void {
      this.loading = true
      this.updateUrl()
      this.customerQuoteService.searchCustomerQuotes(this.pageNumber, this.pageSize, this.sortField, this.buildTypeId, this.cartridgeId, this.ascending).then(pagingResponse => {
        this.totalResults = pagingResponse.totalResults
        this.quotes = pagingResponse.results
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to load your quotes. Try reloading the page.')
      })
    },
    sortBy(sortField: string): void {
      if (this.sortField === sortField)
        this.ascending = !this.ascending
      else
        this.sortField = sortField

      this.searchQuotes()
    },
    updateUrl(): void {
      let newQueryString = `?a=${this.ascending}&pn=${this.pageNumber}&ps=${this.pageSize}&sf=${this.sortField}`

      if (this.buildTypeId !== undefined)
        newQueryString = newQueryString + `&bt=${this.buildTypeId}`

      if (this.cartridgeId !== undefined)
        newQueryString = newQueryString + `&ca=${this.cartridgeId}`
      
      this.$router.replace('/my-quotes' + newQueryString)
    }
  }
})
</script>

<style scoped lang="scss">
.search-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 15px;

  @include sac-search-page;
}
</style>
